import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import {
  api,
  getPosHeaders,
} from "./api";

import { usePermissions } from "./PermissionContext";


const CONNECTION_LABELS = {
  not_configured: "Sin configurar",
  provider: "Proveedor tecnológico",
  direct_dian: "Conexión directa con la DIAN",
};

const ENVIRONMENT_LABELS = {
  test: "Pruebas",
  production: "Producción",
};


function getErrorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return "No fue posible consultar la configuración de facturación.";
}


function formatDate(value) {
  if (!value) return "Sin configurar";

  const parts = String(value).split("-");

  if (parts.length !== 3) {
    return "Fecha inválida";
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}


function ConfigurationField({ label, children }) {
  return (
    <div className="col-12 col-sm-6 col-lg-4">
      <div className="border rounded p-3 h-100">
        <small className="d-block text-muted mb-1">
          {label}
        </small>

        <strong className="text-break">
          {children}
        </strong>
      </div>
    </div>
  );
}


export default function POSInvoiceSettings() {
  const { company } = useParams();
  const { can } = usePermissions();

  const canViewBilling = can("billing.view");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const controllerRef = useRef(null);
  const requestVersionRef = useRef(0);

  const loadSettings = useCallback(async () => {
    controllerRef.current?.abort();

    const requestVersion = ++requestVersionRef.current;

    if (!company || !canViewBilling) {
      setResult(null);
      setError("");
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        "/pos/invoice-settings/me",
        {
          headers: getPosHeaders(company),
          signal: controller.signal,
        }
      );

      if (
        controller.signal.aborted ||
        requestVersion !== requestVersionRef.current
      ) {
        return;
      }

      const data = response.data;

      if (
        !data ||
        typeof data.numbering?.configuration_valid !== "boolean" ||
        !Array.isArray(data.numbering?.issues) ||
        !Array.isArray(data.electronic_connection?.issues)
      ) {
        throw new Error(
          "El servidor devolvió una configuración de facturación inválida."
        );
      }

      setResult({
        company,
        data,
      });
    } catch (requestError) {
      if (
        controller.signal.aborted ||
        requestVersion !== requestVersionRef.current
      ) {
        return;
      }

      setResult(null);

      setError(
        requestError.response
          ? getErrorMessage(requestError)
          : requestError.message ||
              "No fue posible consultar la configuración."
      );
    } finally {
      if (
        !controller.signal.aborted &&
        requestVersion === requestVersionRef.current
      ) {
        setLoading(false);
      }
    }
  }, [company, canViewBilling]);

  useEffect(() => {
    setResult(null);

    loadSettings();

    return () => {
      requestVersionRef.current += 1;
      controllerRef.current?.abort();
    };
  }, [loadSettings]);

  if (!canViewBilling) {
    return null;
  }

  const data =
    result?.company === company
      ? result.data
      : null;

  const settings = data?.settings;
  const numbering = data?.numbering;
  const connection = data?.electronic_connection;

  const numberingIssues = numbering?.issues || [];
  const connectionIssues = connection?.issues || [];

  return (
    <section
      className="border rounded p-3 p-lg-4 mt-3 bg-white"
      aria-label="Configuración de facturación electrónica"
      aria-busy={loading}
    >
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h3 className="h6 mb-1">
            Configuración de facturación
          </h3>

          <p className="small text-muted mb-0">
            Configuración compartida con el sistema principal.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => loadSettings()}
          disabled={loading}
        >
          {loading
            ? "Consultando..."
            : "Actualizar configuración"}
        </button>
      </div>

      {loading && (
        <p className="small text-muted" role="status">
          Consultando resolución y conexión electrónica…
        </p>
      )}

      {error && (
        <div className="alert alert-danger mb-0" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span
              className={`badge ${
                numbering.configuration_valid
                  ? "bg-success"
                  : "bg-warning text-dark"
              }`}
            >
              {numbering.configuration_valid
                ? "Numeración configurada"
                : "Numeración pendiente"}
            </span>

            <span className="badge bg-secondary">
              {CONNECTION_LABELS[connection.connection_mode] ||
                "Conexión desconocida"}
            </span>
          </div>

          <div className="row g-3">
            <ConfigurationField label="Prefijo">
              {settings?.invoice_prefix || "Sin configurar"}
            </ConfigurationField>

            <ConfigurationField label="Próximo consecutivo configurado">
              {settings?.current_number ?? "Sin configurar"}
            </ConfigurationField>

            <ConfigurationField label="Resolución">
              {settings?.resolution_number || "Sin configurar"}
            </ConfigurationField>

            <ConfigurationField label="Rango autorizado">
              {settings?.authorized_from != null &&
              settings?.authorized_to != null
                ? `${settings.authorized_from} – ${settings.authorized_to}`
                : "Sin configurar"}
            </ConfigurationField>

            <ConfigurationField label="Inicio de vigencia">
              {formatDate(settings?.valid_from)}
            </ConfigurationField>

            <ConfigurationField label="Fin de vigencia">
              {formatDate(settings?.valid_until)}
            </ConfigurationField>

            <ConfigurationField label="Consecutivos disponibles">
              {numbering.remaining_numbers ?? 0}
            </ConfigurationField>

            <ConfigurationField label="Ambiente de conexión">
              {ENVIRONMENT_LABELS[connection.environment] ||
                "Sin configurar"}
            </ConfigurationField>

            <ConfigurationField label="Facturación habilitada">
              {settings
                ? settings.is_active
                  ? "Sí"
                  : "No"
                : "Sin configurar"}
            </ConfigurationField>
          </div>

          {numberingIssues.length > 0 && (
            <div className="alert alert-warning mt-3 mb-0">
              <strong>Pendientes de numeración</strong>

              <ul className="mb-0 mt-2">
                {numberingIssues.map((issue, index) => (
                  <li key={`${issue.code}:${index}`}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {connectionIssues.length > 0 && (
            <div className="alert alert-info mt-3 mb-0">
              <strong>Pendientes de conexión electrónica</strong>

              <ul className="mb-0 mt-2">
                {connectionIssues.map((issue, index) => (
                  <li key={`${issue.code}:${index}`}>
                    {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="small text-muted mt-3 mb-0">
            El consecutivo mostrado es informativo y puede cambiar
            cuando otro usuario emita una factura. Esta consulta
            no reserva números ni transmite documentos.
          </p>
        </>
      )}
    </section>
  );
}